import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { updateAccessible } from '../../../../utils/services/complexDocumentsAPI';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../../context/complexArtboardContext';
import { DESIGN_AREA_SETTINGS_TAB } from '../../../SidebarMenu/translationValues';

const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];

export const useAccessibilitySettings = () => {
  const { t } = useTranslation(NAMESPACES);
  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId, store] = useDocumentContext(documentIdSelector, 'useAccessibilitySettings');
  
  const accessibilitySelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_ACCESSIBLE], []);
  const [accessibilityEnabled] = useDocumentContext(accessibilitySelector, 'useAccessibilitySettings');
  
  const designSummarySelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY], []);
  const [summary] = useDocumentContext(designSummarySelector, 'useAccessibilitySettings');
  
  const [, methods] = useDocumentContext(()=>null, 'useAccessibilitySettings');
  
  const isOpenDesignSelector = useCallback((store) => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign, updateArtboard] = useArtboardContext(isOpenDesignSelector, 'useAccessibilitySettings');
  
  const [isAccessible, setIsAccessible] = useState(accessibilityEnabled);

  const handleOpenDesign = useCallback(() => {
    updateArtboard({
      [CONTEXT_KEYS.SHOW_SIGNUP_PROMPT]: true
    });
  }, [updateArtboard]);

  const onAccessibleChange = useCallback(
    async check => {
      if (isOpenDesign) {
        handleOpenDesign();
        return;
      }
      setIsAccessible(check);
      const showErrorMessage = () => {
        if (check) {
          return message.error(
            t(
              DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_ENABLE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_ENABLE_FAIL_ERROR_MESSAGE.value,
            )
          );
        }
        return message.error(
          t(
              DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_DISABLE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY_DISABLE_FAIL_ERROR_MESSAGE.value,
            )
        );
      };
      try {
        const response = await updateAccessible(documentId, check);
        if (response?.statusCode === 849) {
          store.set({
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_ACCESSIBLE]: check,
          });
        } else {
          setIsAccessible(!check);
          showErrorMessage();
        }
      } catch (e) {
        setIsAccessible(!check);
        showErrorMessage();
      }
    },
    [documentId, isOpenDesign, handleOpenDesign, store, t],
  );

  const onDocumentSummarySave = useCallback(({contents, newGlobalVariables}) => {
    methods.updateSummary(contents, newGlobalVariables);
  }, [methods]);

  return {
    isAccessible,
    summary,
    onAccessibleChange,
    onDocumentSummarySave,
  };
};


